import React from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  TestsFull_tests_pagination,
  TestsFull_tests_tests,
  getFormattedDateAndTime,
  getResultLabelColor,
  useAuth,
  UserRole,
  SortByType,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { DownloadTestResultPdfButton } from 'sharedComponents/DownloadTestResultPdfButton'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Label from 'components/Label'
import Card from 'components/Card'
import { isInfluenza } from 'utils/helpers'

interface ColumnLabels {
  groupCompany?: string | null
  patient?: string | null
  staff?: string | null
  verified?: string | null
  result?: string | null
  resultReceivedAt?: string | null
  testedAt?: string | null
  resultProvisionedAt?: string | null
  location?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  tests: TestsFull_tests_tests[]
  pagination?: TestsFull_tests_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
  onRowClick?: (data: TestsFull_tests_tests) => void
}

const defaultColumnLabels = {
  groupCompany: 'Group (Company)',
  patient: 'Patient',
  staff: 'Staff',
  verified: 'Verified by CLS',
  result: 'Result',
  resultReceivedAt: 'Speciment Received at',
  testedAt: 'Specimen Collected At',
  resultProvisionedAt: 'Released at',
  location: 'Location',
}

const PAGE_LENGTH = 10

export const TestsDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  tests,
  sorter,
  pagination,
  isLoading,
  columnLabels: providedColumnLabels,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])

  const { hasRoles } = useAuth()
  const isSuperAdmin = hasRoles([UserRole.SUPER_ADMIN])
  const isCLS = hasRoles([UserRole.CLS])

  // Helper function to determine if a test row should be visible
  const shouldShowTestRow = (test: TestsFull_tests_tests): boolean => {
    // Super admins and CLS can see all rows
    if (isSuperAdmin || isCLS) {
      return true
    }

    // For other users, apply visibility rules
    const { resultManuallyEntered, resultVerified } = test

    // If result was manually entered, only show if it's verified
    if (resultManuallyEntered === true) {
      return resultVerified === true
    }

    // If result was not manually entered (false or null), show the row
    return true
  }

  // Filter tests based on visibility rules
  const visibleTests = tests.filter(shouldShowTestRow)

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={visibleTests}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.groupCompany) ? (
                <Datalist.HeaderCell label={columnLabels.groupCompany} />
              ) : null}
              {!isNull(columnLabels.patient) ? (
                <Datalist.HeaderCell label={columnLabels.patient} />
              ) : null}
              {!isNull(columnLabels.staff) ? (
                <Datalist.HeaderCell label={columnLabels.staff} />
              ) : null}
              {!isNull(columnLabels.verified) ? (
                <Datalist.HeaderCell label={columnLabels.verified} />
              ) : null}
              {!isNull(columnLabels.result) ? (
                <Datalist.HeaderCell sortKey={SortByType.RESULT} label={columnLabels.result} />
              ) : null}
              {!isNull(columnLabels.testedAt) ? (
                <Datalist.HeaderCell sortKey={SortByType.TESTED_AT} label={columnLabels.testedAt} />
              ) : null}
              {!isNull(columnLabels.resultReceivedAt) ? (
                <Datalist.HeaderCell
                  sortKey={SortByType.RESULT_RECIEVED_AT}
                  label={columnLabels.resultReceivedAt}
                />
              ) : null}
              {!isNull(columnLabels.resultProvisionedAt) ? (
                <Datalist.HeaderCell
                  sortKey={SortByType.RESULT_PROVISIONED_AT}
                  label={columnLabels.resultProvisionedAt}
                />
              ) : null}
              {!isNull(columnLabels.location) ? (
                <Datalist.HeaderCell label={columnLabels.location} />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const {
              id,
              testedAt,
              result,
              resultVerified,
              resultManuallyEntered,
              secondResult,
              resultReceivedAt,
              resultProvisionedAt,
              TestSession,
              StaffMembers,
              ParticipantMembers,
            } = data
            const [participant] = ParticipantMembers
            const [staff] = StaffMembers
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(data)
                      }
                    : undefined
                }
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.groupCompany) ? (
                  <Datalist.Cell label={columnLabels.groupCompany}>
                    {TestSession.Groups?.map(({ name }) => name).join(', ') || ''}{' '}
                    {TestSession.Company ? `(${TestSession.Company.name})` : ''}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.patient) ? (
                  <Datalist.Cell label={columnLabels.patient}>
                    {participant && (
                      <>
                        {participant.User.firstName} {participant.User.lastName} (
                        {getFormattedDateAndTime(participant.User.birthDate)})
                      </>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.staff) ? (
                  <Datalist.Cell label={columnLabels.staff}>
                    {staff && (
                      <>
                        {staff.User.firstName} {staff.User.lastName}
                      </>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.verified) ? (
                  <Datalist.Cell label={columnLabels.verified}>
                    {resultVerified === true ? (
                      <Label indicator="green">Verified</Label>
                    ) : !result ? (
                      <Label indicator="yellow">No result to verify</Label>
                    ) : !resultManuallyEntered && result ? (
                      <Label indicator="green">Non-manual result</Label>
                    ) : (
                      <Label indicator="red">Not verified</Label>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.result) ? (
                  <Datalist.Cell label={columnLabels.result}>
                    <Label indicator={result ? getResultLabelColor(result) : 'blue'}>
                      {TestSession.type && isInfluenza(TestSession.type) && `A: `}
                      {result || 'Pending result'}
                    </Label>
                    {TestSession.type && isInfluenza(TestSession.type) && secondResult && (
                      <Label indicator={getResultLabelColor(secondResult)}>
                        {`B: ${secondResult}`}
                      </Label>
                    )}
                    {result && (
                      <DownloadTestResultPdfButton testId={id} appearance="ghost" size="small" />
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.testedAt) ? (
                  <Datalist.Cell label={columnLabels.testedAt}>
                    {getFormattedDateAndTime(testedAt, true)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.resultReceivedAt) ? (
                  <Datalist.Cell label={columnLabels.resultReceivedAt}>
                    {resultReceivedAt ? (
                      getFormattedDateAndTime(resultReceivedAt, true)
                    ) : (
                      <Label indicator="red">Not received yet</Label>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.resultProvisionedAt) ? (
                  <Datalist.Cell label={columnLabels.resultProvisionedAt}>
                    {!result ? (
                      'Pending result'
                    ) : (
                      <>
                        {resultProvisionedAt ? (
                          getFormattedDateAndTime(resultProvisionedAt, true)
                        ) : (
                          <Label indicator="red">Not released yet</Label>
                        )}
                      </>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.location) ? (
                  <Datalist.Cell label={columnLabels.location}>
                    {TestSession?.Location.city || ''}
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
    </>
  )
}
